// Instagram API with Facebook SDK Configuration
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '3831389470484286';

// Initialize Facebook SDK
export const initFacebookSDK = () => {
  return new Promise((resolve) => {
    // If FB is already loaded, resolve immediately
    if (window.FB) {
      console.log('✅ Facebook SDK already available');
      resolve(window.FB);
      return;
    }

    // Wait for SDK to be available
    const checkFB = () => {
      if (window.FB) {
        console.log('✅ Facebook SDK is now available');
        resolve(window.FB);
      } else {
        console.log('⏳ Still waiting for Facebook SDK...');
        setTimeout(checkFB, 100);
      }
    };

    checkFB();
  });
};

// Facebook Login for Instagram API access
export const loginWithFacebook = () => {
  return new Promise((resolve, reject) => {
    initFacebookSDK().then((FB) => {
      FB.login(function(response) {
        if (response.authResponse) {
          console.log('✅ Facebook login successful');
          console.log('Access Token:', response.authResponse.accessToken);
          resolve(response.authResponse);
        } else {
          console.log('❌ Facebook login failed');
          reject(new Error('Facebook login was cancelled or failed'));
        }
      }, {
        scope: 'pages_show_list,pages_read_engagement,instagram_basic,instagram_manage_insights,business_management'
      });
    });
  });
};

// Get user's Facebook pages and Instagram business accounts
export const getInstagramBusinessAccounts = async (accessToken) => {
  try {
    // Get user's Facebook pages
    const pagesResponse = await fetch(`https://graph.facebook.com/v23.0/me/accounts?access_token=${accessToken}`);
    
    if (!pagesResponse.ok) {
      const errorData = await pagesResponse.json();
      throw new Error(`Failed to fetch pages: ${errorData.error?.message || pagesResponse.statusText}`);
    }
    
    const pagesData = await pagesResponse.json();
    console.log('📄 Facebook Pages found:', pagesData.data?.length || 0);
    
    const instagramAccounts = [];
    
    // Check each page for Instagram business account
    for (const page of pagesData.data || []) {
      try {
        const igResponse = await fetch(`https://graph.facebook.com/v23.0/${page.id}?fields=instagram_business_account&access_token=${accessToken}`);
        const igData = await igResponse.json();
        
        if (igData.instagram_business_account) {
          // Get Instagram account details
          const profileResponse = await fetch(`https://graph.facebook.com/v23.0/${igData.instagram_business_account.id}?fields=id,username,name,profile_picture_url,followers_count,media_count,account_type&access_token=${accessToken}`);
          const profile = await profileResponse.json();
          
          console.log('📊 Instagram Profile Data:', profile);
          
          // Create simple initial placeholder
          const firstLetter = (profile.username || page.name || 'U').charAt(0).toUpperCase();
          
          instagramAccounts.push({
            id: profile.id || igData.instagram_business_account.id,
            username: profile.username ? `@${profile.username}` : (page.name ? `@${page.name.toLowerCase().replace(/\s+/g, '')}` : 'Instagram User'),
            fullName: profile.name || page.name || profile.username || 'Instagram User',
            accountType: profile.account_type || 'business',
            profilePic: `https://ui-avatars.com/api/?name=${firstLetter}&background=7c3aed&color=fff&size=150`,
            followersCount: profile.followers_count || 0,
            mediaCount: profile.media_count || 0,
            platform: 'instagram',
            verified: false,
            pageId: page.id,
            pageName: page.name
          });
        }
      } catch (error) {
        console.log(`No Instagram account found for page: ${page.name}`);
      }
    }
    
    return instagramAccounts;
  } catch (error) {
    console.error('Instagram business accounts fetch error:', error);
    throw error;
  }
};

// Get user's basic Facebook profile (fallback)
export const getFacebookProfile = async (accessToken) => {
  try {
    const response = await fetch(`https://graph.facebook.com/v23.0/me?fields=id,name,picture&access_token=${accessToken}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to fetch Facebook profile: ${errorData.error?.message || response.statusText}`);
    }
    
    const profile = await response.json();
    
    const firstLetter = profile.name.charAt(0).toUpperCase();
    
    return {
      id: profile.id,
      username: profile.name,
      fullName: profile.name,
      accountType: 'personal',
      profilePic: `https://ui-avatars.com/api/?name=${firstLetter}&background=7c3aed&color=fff&size=150`,
      platform: 'facebook',
      verified: false
    };
  } catch (error) {
    console.error('Facebook profile fetch error:', error);
    throw error;
  }
};

// Complete Instagram login flow using Facebook SDK
export const handleInstagramLogin = async () => {
  try {
    console.log('🔄 Starting Instagram login via Facebook SDK...');
    
    // Step 1: Login with Facebook
    const authResponse = await loginWithFacebook();
    const accessToken = authResponse.accessToken;
    
    // Step 2: Get Instagram business accounts
    console.log('🔄 Fetching Instagram business accounts...');
    const instagramAccounts = await getInstagramBusinessAccounts(accessToken);
    
    let userProfile;
    
    if (instagramAccounts.length > 0) {
      // Use first Instagram business account found
      userProfile = instagramAccounts[0];
      console.log('✅ Instagram business account found:', userProfile.username);
    } else {
      // Fallback to Facebook profile
      console.log('⚠️ No Instagram business accounts found, using Facebook profile');
      userProfile = await getFacebookProfile(accessToken);
    }
    
    // Step 3: Store user data locally
    localStorage.setItem('instagram_user', JSON.stringify(userProfile));
    localStorage.setItem('facebook_token', accessToken);
    localStorage.setItem('instagram_accounts', JSON.stringify(instagramAccounts));
    
    // Step 4: Send Facebook token to backend for business discovery
    try {
      console.log('🔄 Storing Facebook token on backend for business discovery...');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071';
  const response = await fetch(`${API_BASE_URL}/api/facebook/set-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          facebookToken: accessToken,
          expiresIn: 5184000 // 60 days default
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Facebook token stored on backend:', result.message);
      } else {
        console.warn('⚠️ Failed to store Facebook token on backend, but login will continue');
      }
    } catch (error) {
      console.warn('⚠️ Could not store Facebook token on backend:', error.message);
    }
    
    return { 
      userProfile, 
      instagramAccounts,
      accessToken,
      success: true 
    };
  } catch (error) {
    console.error('❌ Instagram login via Facebook error:', error);
    throw error;
  }
};

// Check if user is already logged in
export const checkLoginStatus = () => {
  return new Promise((resolve) => {
    initFacebookSDK().then((FB) => {
      FB.getLoginStatus(function(response) {
        if (response.status === 'connected') {
          console.log('✅ User already logged in to Facebook');
          resolve(response.authResponse);
        } else {
          console.log('❌ User not logged in');
          resolve(null);
        }
      });
    });
  });
};

// Logout function
export const logout = () => {
  return new Promise((resolve) => {
    initFacebookSDK().then((FB) => {
      // Clear local storage first
      localStorage.removeItem('instagram_user');
      localStorage.removeItem('facebook_token');
      localStorage.removeItem('instagram_accounts');
      
      // Try to logout from Facebook if connected
      FB.getLoginStatus(function(response) {
        if (response.status === 'connected') {
          FB.logout(function(logoutResponse) {
            console.log('✅ User logged out from Facebook');
            resolve(logoutResponse);
          });
        } else {
          console.log('✅ User logged out (not connected to Facebook)');
          resolve({ status: 'not_authorized' });
        }
      });
    }).catch((error) => {
      // If SDK fails, still clear localStorage
      localStorage.removeItem('instagram_user');
      localStorage.removeItem('facebook_token');
      localStorage.removeItem('instagram_accounts');
      console.log('✅ User logged out (SDK unavailable)');
      resolve({ status: 'sdk_unavailable' });
    });
  });
}; 